/* @jsxImportSource @emotion/react */
import React, { useEffect, useState } from 'react'
import { keyframes } from '@emotion/react'

import { defaultSliteCustomerList } from './constants'

const SMALL_BREAKPOINT = 640 // 40rem

type CustomerLogo = {
  name: string
  logo: (props: React.SVGProps<SVGSVGElement>) => React.JSX.Element
  // The tool this customer migrated to Slite from (e.g. "Confluence", "Notion").
  // Used for "migrated from X" callouts in the hero grid.
  migratedFrom?: string
}

type CustomerLogoBannerProps = {
  logos?: CustomerLogo[]
  logoHeight?: number
  containerClass?: string
  animated?: boolean
  showEdgeFade?: boolean
}

const slideAnimation = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
`

export function CustomerLogoBanner({
  logos = defaultSliteCustomerList,
  logoHeight = 24,
  containerClass,
  animated = true,
  showEdgeFade = true,
}: CustomerLogoBannerProps) {
  // SSR: render logos once to keep HTML small (~950KB savings from not tripling inline SVGs).
  // Client: after hydration, duplicate for seamless infinite scroll animation.
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
  }, [])

  const displayLogos = animated
    ? mounted
      ? [...logos, ...logos, ...logos]
      : logos
    : logos.slice(0, 4)

  return (
    <div
      className={containerClass}
      css={{
        position: 'relative',
        overflow: 'hidden',
        ...(animated &&
          showEdgeFade && {
            maskImage:
              'linear-gradient(to right, transparent, black 15%, black 85%, transparent)',
            WebkitMaskImage:
              'linear-gradient(to right, transparent, black 15%, black 85%, transparent)',
          }),
      }}
    >
      <div
        css={{
          display: 'flex',
          ...(animated &&
            mounted && {
              animation: `${slideAnimation} 120s linear infinite`,
              willChange: 'transform',
              width: 'max-content',
            }),
          ...(animated &&
            !mounted && {
              width: '100%',
              justifyContent: 'center',
              overflow: 'hidden',
            }),
          ...(!animated && {
            width: '100%',
            justifyContent: 'center',
          }),
        }}
      >
        {displayLogos.map(({ logo: CustomerLogo, name }, index) => (
          <div
            key={`logo-${name}-${index}`}
            css={{
              flex: '0 0 auto',
              padding: animated ? 20 : 16,
              minWidth: animated ? 180 : 120,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              // The wide fixed cells leave short logos floating far apart on
              // phones; let content set the rhythm there instead.
              ...(animated && {
                [`@media (max-width: ${SMALL_BREAKPOINT}px)`]: {
                  padding: 8,
                  minWidth: 0,
                },
              }),
            }}
          >
            <CustomerLogo
              css={{
                height: logoHeight,
                display: 'inline-block',
                flex: 'none',
                objectFit: 'contain',
                margin: 'auto',
                maxWidth: animated ? 130 : 100,
              }}
              className="margin-horizontal-16 text-align-center margin-vertical-12"
            />
          </div>
        ))}
      </div>
    </div>
  )
}
